import { http } from 'uview-pro'
import { sm2 } from 'sm-crypto'
import type { LoginResult } from '@/types'
import { validateNewPassword as validateNewPasswordRule } from '@/common/utils'

/**
 * SM2 公钥加密模式：
 * 1 = C1C3C2（hutool/主流 Java 后端默认）
 * 0 = C1C2C3（老格式，后端解密失败时自动降级重试）
 */
const SM2_CIPHER_MODES: Array<0 | 1> = [1, 0]

/** 获取 SM2 公钥 */
async function fetchPublicKey(): Promise<string> {
  const data: any = await http.get('/auth/public-key', {}, { meta: { loading: false } })
  const publicKey = data?.public_key || data?.publicKey
  if (!publicKey)
    throw new Error('获取公钥失败')
  return publicKey as string
}

/**
 * 账号密码登录：
 * 1. GET /auth/public-key 拿 SM2 公钥
 * 2. SM2 加密密码（C1C3C2 失败自动降级 C1C2C3）
 * 3. POST /auth/login { account, password(密文) }
 */
export async function apiLogin(account: string, password: string): Promise<LoginResult> {
  const publicKey = await fetchPublicKey()

  let lastError: Error | null = null
  for (const mode of SM2_CIPHER_MODES) {
    const encrypted = sm2.doEncrypt(password, publicKey, mode)
    try {
      const data = await http.post<LoginResult>('/auth/login', { account, password: encrypted })
      return {
        token: data.token,
        refreshToken: (data as any).refresh_token,
        account: data.account ?? account,
        nickname: data.nickname ?? account,
        points: data.points ?? 0,
      }
    }
    catch (err: any) {
      lastError = err
      // 非解密类错误（如账号密码错误）直接抛出，不再换模式重试
      if (!/解密|加密|SM2/.test(err?.message || '')) {
        throw err
      }
    }
  }
  throw lastError ?? new Error('登录失败')
}

/** 当前用户信息（含最新积分余额） */
export async function apiFetchProfile(): Promise<{ account: string, nickname: string, points: number }> {
  return await http.get('/user/profile')
}

/** 注册结果 */
export interface RegisterResult {
  id: number
  account: string
  nickname: string
  points: number
}

/**
 * 普通用户注册：
 * POST /auth/register { account, password(SM2 密文) }
 * 服务端校验密码复杂度（6 条规则，见 common/utils validateNewPassword）
 */
export async function apiRegister(account: string, password: string): Promise<RegisterResult> {
  const publicKey = await fetchPublicKey()
  const encrypted = sm2.doEncrypt(password, publicKey, 1)
  return await http.post<RegisterResult>('/auth/register', {
    account,
    password: encrypted,
  })
}

/**
 * 自助修改密码（需登录）：
 * PUT /auth/password/change { oldPassword, newPassword }（均为 SM2 密文）
 * 服务端错误：1002 解密失败/原密码错误/复杂度不达标
 */
export async function apiChangePassword(oldPassword: string, newPassword: string): Promise<void> {
  const publicKey = await fetchPublicKey()
  await http.put('/auth/password/change', {
    oldPassword: sm2.doEncrypt(oldPassword, publicKey, 1),
    newPassword: sm2.doEncrypt(newPassword, publicKey, 1),
  })
}

/**
 * 密码复杂度校验（与后端同一套 6 条规则，注册/改密共用）
 * @returns null=通过，否则返回错误提示
 */
export function validateNewPassword(password: string, account = ''): string | null {
  return validateNewPasswordRule(password, account)
}
